import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Minimum certification keys all drivers must have in order to receive move offers.
 */
export const schema = z
  .array(z.string())
  .describe('Minimum certification keys all drivers must have in order to receive move offers.');

export type MinimumCertificationKeys = z.infer<typeof schema>;

export const definition = define({
  key: 'minimum_certification_ids',
  kind: DEFINITION_KINDS.SETTING,
  schema,
  writableAtLevels: [
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.RATERULEGROUP,
    LEVELS.MOVE,
  ],
  readableByRoles: [ROLES.ADMIN],
  label: 'Minimum Certification IDs',
  description: 'Minimum certification IDs all drivers must have in order to be receive move offers.',
  category: CATEGORIES.CUSTOM_SETTINGS.key,

  storage: {
    enum: {
      from: 'certifications',
      select: {
        key: true,
        name: true,
        description: true,
        requires_documents: true,
        requires_expiration: true,
        notes: true,
      },
      orderBy: { name: 'asc' },
      mapping: {
        value: 'key',
        label: 'name',
        description: 'description',
        metadata: {
          requires_documents: 'requires_documents',
          requires_expiration: 'requires_expiration',
          notes: 'notes',
        },
      },
    },
    levels: {
      region: {
        type: 'link_table',
        table: 'regionstocertifications',
        foreignKey: 'region_id',
        valueKey: 'certification_key',
        auditColumns: {
          createdByColumn: 'createdby',
          updatedByColumn: 'updatedby',
          createdAtColumn: 'created_at',
          updatedAtColumn: 'updated_at',
        },
      },
      organization: {
        type: 'link_table',
        table: 'organizationstocertifications',
        foreignKey: 'organization_id',
        valueKey: 'certification_key',
        auditColumns: {
          createdByColumn: 'createdby',
          updatedByColumn: 'updatedby',
          createdAtColumn: 'createdat',
          updatedAtColumn: 'updated_at',
        },
      },
      customer: {
        type: 'link_table',
        table: 'customerstocertifications',
        foreignKey: 'customer_id',
        valueKey: 'certification_key',
        auditColumns: {
          createdByColumn: 'createdby',
          updatedByColumn: 'updatedby',
          createdAtColumn: 'createdat',
          updatedAtColumn: 'updated_at',
        },
      },
    },
  },

  defaultValue: [],
});
